import React, { useEffect, useState } from 'react';
import { useAdminStore } from '@/store/adminStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  CreditCard,
  Building,
  Settings2,
  Plus,
  Trash2,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Wallet,
  Key,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/components/I18nProvider';

export default function AdminPaymentMethodsPage() {
  const { t } = useI18n();
  const { paymentMethods, isLoading, fetchPaymentMethods, createPaymentMethod, updatePaymentMethod, deletePaymentMethod } = useAdminStore();
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showKeys, setShowKeys] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const openEdit = (method) => {
    setEditForm({
      name: method.name || '',
      description: method.description || '',
      is_active: method.is_active || false,
      config: { ...(method.config || {}) },
    });
    setShowKeys(false);
    setEditDialog(method);
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await updatePaymentMethod(editDialog.id, editForm);
    setSaving(false);
    if (res.success) {
      toast.success('Ödeme yöntemi güncellendi');
      setEditDialog(null);
      fetchPaymentMethods();
    } else {
      toast.error(res.error || 'Güncelleme başarısız');
    }
  };

  const handleToggleActive = async (method) => {
    // For credit card, check if API keys are configured
    if (method.type === 'credit_card' && !method.is_active) {
      const cfg = method.config || {};
      if (!cfg.merchant_id || !cfg.merchant_key || !cfg.merchant_salt) {
        toast.error('Kredi kartı ödemesini aktif etmek için önce PayTR API bilgilerini girin');
        return;
      }
    }
    // For bank transfer, check if IBAN is configured
    if (method.type === 'bank_transfer' && !method.is_active) {
      const cfg = method.config || {};
      if (!cfg.iban || !cfg.bank_name) {
        toast.error('Havale/EFT ödemesini aktif etmek için önce banka bilgilerini girin');
        return;
      }
    }
    const res = await updatePaymentMethod(method.id, { is_active: !method.is_active });
    if (res.success) {
      toast.success(method.is_active ? 'Ödeme yöntemi pasif edildi' : 'Ödeme yöntemi aktif edildi');
      fetchPaymentMethods();
    } else {
      toast.error(res.error);
    }
  };

  const handleDelete = async (method) => {
    if (!window.confirm(`"${method.name}" ödeme yöntemini silmek istediğinize emin misiniz?`)) return;
    const res = await deletePaymentMethod(method.id);
    if (res.success) toast.success('Silindi');
    else toast.error(res.error);
  };

  const handleAddMethod = async (type) => {
    const data = type === 'bank_transfer'
      ? { type: 'bank_transfer', name: 'Havale / EFT', description: 'Banka havalesi ile ödeme', config: { bank_name: '', iban: '', account_holder: '' }, sort_order: paymentMethods.length }
      : { type: 'credit_card', name: 'Kredi Kartı', description: 'PayTR ile güvenli ödeme', config: { merchant_id: '', merchant_key: '', merchant_salt: '', test_mode: true }, sort_order: paymentMethods.length };
    const res = await createPaymentMethod(data);
    if (res.success) {
      toast.success('Ödeme yöntemi eklendi');
      fetchPaymentMethods();
    } else {
      toast.error(res.error);
    }
  };

  const maskValue = (val) => {
    if (!val || showKeys) return val || '';
    if (val.length <= 6) return '••••••';
    return val.substring(0, 3) + '•'.repeat(val.length - 6) + val.substring(val.length - 3);
  };

  const creditCards = paymentMethods.filter(m => m.type === 'credit_card');
  const bankTransfers = paymentMethods.filter(m => m.type === 'bank_transfer');

  if (isLoading && paymentMethods.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="admin-payment-methods-page">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Ödeme Yöntemleri</h1>
        <div className="space-y-4">{[1, 2].map(i => <Skeleton key={i} className="h-48 w-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-payment-methods-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Ödeme Yöntemleri</h1>
        <p className="mt-1 text-sm text-muted-foreground">Kayıt sırasında gösterilecek ödeme yöntemlerini yönetin</p>
      </div>

      {/* Credit Card Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-2.5 text-blue-600">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Kredi Kartı (PayTR)</CardTitle>
                <CardDescription>PayTR ödeme altyapısı ile kredi kartı ödemesi alın</CardDescription>
              </div>
            </div>
            {creditCards.length === 0 && (
              <Button size="sm" onClick={() => handleAddMethod('credit_card')} data-testid="add-credit-card-btn">
                <Plus className="h-4 w-4 mr-1" />Ekle
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {creditCards.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              Henüz kredi kartı ödeme yöntemi eklenmemiş
            </div>
          ) : (
            creditCards.map(method => {
              const cfg = method.config || {};
              const hasApi = cfg.merchant_id && cfg.merchant_key && cfg.merchant_salt;
              return (
                <div key={method.id} className="space-y-4" data-testid={`payment-method-${method.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {method.is_active ? (
                        <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />{t('active')}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground"><XCircle className="h-3 w-3 mr-1" />{t('inactive')}</Badge>
                      )}
                      {hasApi ? (
                        <Badge className="bg-blue-100 text-blue-700"><Key className="h-3 w-3 mr-1" />API Bağlı</Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300"><AlertTriangle className="h-3 w-3 mr-1" />API Gerekli</Badge>
                      )}
                      {cfg.test_mode && hasApi && (
                        <Badge variant="outline" className="text-xs">Test Modu</Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Switch
                        checked={method.is_active}
                        onCheckedChange={() => handleToggleActive(method)}
                        data-testid={`toggle-cc-${method.id}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => openEdit(method)} data-testid={`edit-cc-${method.id}`}>
                        <Settings2 className="h-4 w-4 mr-1" />Ayarlar
                      </Button>
                    </div>
                  </div>
                  {!hasApi && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      <p className="font-medium flex items-center gap-1"><AlertTriangle className="h-4 w-4" />PayTR API bilgileri gerekli</p>
                      <p className="text-xs mt-1">Kredi kartı ile ödeme alabilmek için PayTR Merchant ID, Key ve Salt bilgilerini girin. Bu bilgileri PayTR panelinden alabilirsiniz.</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Bank Transfer Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-100 p-2.5 text-green-600">
                <Building className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Havale / EFT</CardTitle>
                <CardDescription>Banka hesap bilgilerini girerek havale ile ödeme alın</CardDescription>
              </div>
            </div>
            <Button size="sm" onClick={() => handleAddMethod('bank_transfer')} data-testid="add-bank-transfer-btn">
              <Plus className="h-4 w-4 mr-1" />IBAN Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {bankTransfers.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              Henüz banka hesabı eklenmemiş
            </div>
          ) : (
            bankTransfers.map(method => {
              const cfg = method.config || {};
              return (
                <div key={method.id} className="rounded-lg border p-4 space-y-3" data-testid={`payment-method-${method.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{cfg.bank_name || 'Banka'}</span>
                      {method.is_active ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px]">{t('active')}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground text-[10px]">{t('inactive')}</Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Switch
                        checked={method.is_active}
                        onCheckedChange={() => handleToggleActive(method)}
                        data-testid={`toggle-bank-${method.id}`}
                      />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(method)} data-testid={`edit-bank-${method.id}`}>
                        <Settings2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(method)} data-testid={`delete-bank-${method.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {cfg.iban && (
                    <div className="text-sm space-y-1">
                      <p className="text-muted-foreground">IBAN: <span className="font-mono font-medium text-foreground">{cfg.iban}</span></p>
                      {cfg.account_holder && <p className="text-muted-foreground">Hesap Sahibi: <span className="font-medium text-foreground">{cfg.account_holder}</span></p>}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-payment-method-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editDialog?.type === 'credit_card' ? <CreditCard className="h-5 w-5 text-blue-600" /> : <Building className="h-5 w-5 text-green-600" />}
              {editDialog?.type === 'credit_card' ? 'Kredi Kartı Ayarları' : 'Havale/EFT Ayarları'}
            </DialogTitle>
            <DialogDescription>
              {editDialog?.type === 'credit_card' ? 'PayTR API bilgilerini girin' : 'Banka hesap bilgilerini girin'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Görünen İsim</Label>
              <Input
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="pm-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('description')}</Label>
              <Input
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                data-testid="pm-desc-input"
              />
            </div>

            <Separator />

            {editDialog?.type === 'credit_card' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">PayTR API Bilgileri</Label>
                  <Button variant="ghost" size="sm" onClick={() => setShowKeys(!showKeys)}>
                    {showKeys ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    {showKeys ? 'Gizle' : 'Göster'}
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Merchant ID</Label>
                  <Input
                    type={showKeys ? 'text' : 'password'}
                    value={editForm.config?.merchant_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, config: { ...editForm.config, merchant_id: e.target.value } })}
                    placeholder="PayTR Merchant ID"
                    data-testid="pm-merchant-id"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Merchant Key</Label>
                  <Input
                    type={showKeys ? 'text' : 'password'}
                    value={editForm.config?.merchant_key || ''}
                    onChange={(e) => setEditForm({ ...editForm, config: { ...editForm.config, merchant_key: e.target.value } })}
                    placeholder="PayTR Merchant Key"
                    data-testid="pm-merchant-key"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Merchant Salt</Label>
                  <Input
                    type={showKeys ? 'text' : 'password'}
                    value={editForm.config?.merchant_salt || ''}
                    onChange={(e) => setEditForm({ ...editForm, config: { ...editForm.config, merchant_salt: e.target.value } })}
                    placeholder="PayTR Merchant Salt"
                    data-testid="pm-merchant-salt"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editForm.config?.test_mode || false}
                    onCheckedChange={(v) => setEditForm({ ...editForm, config: { ...editForm.config, test_mode: v } })}
                    data-testid="pm-test-mode"
                  />
                  <Label className="text-sm">Test Modu</Label>
                </div>
                <p className="text-[11px] text-muted-foreground">PayTR panelinden Merchant ID, Key ve Salt bilgilerinizi alabilirsiniz. Test modunda gerçek ödeme alınmaz.</p>
              </div>
            )}

            {editDialog?.type === 'bank_transfer' && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Banka Bilgileri</Label>
                <div className="space-y-1.5">
                  <Label className="text-xs">Banka Adı</Label>
                  <Input
                    value={editForm.config?.bank_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, config: { ...editForm.config, bank_name: e.target.value } })}
                    placeholder="Örn: Ziraat Bankası"
                    data-testid="pm-bank-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">IBAN</Label>
                  <Input
                    value={editForm.config?.iban || ''}
                    onChange={(e) => setEditForm({ ...editForm, config: { ...editForm.config, iban: e.target.value } })}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    className="font-mono"
                    data-testid="pm-iban"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Hesap Sahibi</Label>
                  <Input
                    value={editForm.config?.account_holder || ''}
                    onChange={(e) => setEditForm({ ...editForm, config: { ...editForm.config, account_holder: e.target.value } })}
                    placeholder="Hesap sahibinin adı"
                    data-testid="pm-account-holder"
                  />
                </div>
              </div>
            )}

            <Separator />

            <div className="flex items-center gap-2">
              <Switch
                checked={editForm.is_active || false}
                onCheckedChange={(v) => setEditForm({ ...editForm, is_active: v })}
                data-testid="pm-active-toggle"
              />
              <Label className="text-sm">{t('active')}</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>{t('cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-payment-method-btn">
              <Save className="h-4 w-4 mr-1" />{saving ? 'Kaydediliyor...' : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
